'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BaiduMap from '@/components/BaiduMap';

interface SystemSetting {
  key: string;
  value: string;
  label: string;
  type: string;
  group: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  backgroundImage: string;
}

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });

  const [contactSettings, setContactSettings] = useState<SystemSetting[]>([]);
  const [contactCategory, setContactCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  
  // 使用fetch API获取系统设置数据的状态
  const [settingsData, setSettingsData] = useState<any>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(true);
  
  // 使用fetch API获取联系我们分类数据的状态
  const [categoriesData, setCategoriesData] = useState<any>(null);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  
  // 使用fetch API获取系统设置数据
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setSettingsLoading(true);
        const response = await fetch('/api/settings');
        const result = await response.json();
        
        if (result && 'success' in result && result.success) {
          setSettingsData(result);
          // 筛选出联系相关的设置
          const contactData = result.data.filter((setting: SystemSetting) => 
            setting.group === 'contact'
          );
          setContactSettings(contactData);
        } else {
          setSettingsError('获取系统设置失败');
        }
      } catch (error) {
        console.error('获取系统设置失败:', error);
        setSettingsError('网络错误，请稍后重试');
      } finally {
        setSettingsLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // 使用fetch API获取联系我们分类数据
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await fetch('/api/categories');
        const result = await response.json();
        
        if (result && 'success' in result && result.success) {
          setCategoriesData(result);
          // 查找联系我们分类（slug为lianxiwomen）
          const contactCategoryData = result.data.find((category: Category) => 
            category.slug === 'lianxiwomen'
          );
          setContactCategory(contactCategoryData || null);
        } else {
          setCategoriesError('获取联系我们分类失败');
        }
      } catch (error) {
        console.error('获取联系我们分类失败:', error);
        setCategoriesError('网络错误，请稍后重试');
      } finally {
        setCategoriesLoading(false);
      }
    };
    
    fetchCategories();
  }, []);
  
  // 处理加载状态
  useEffect(() => {
    setLoading(settingsLoading || categoriesLoading);
  }, [settingsLoading, categoriesLoading]);

  // 获取特定设置的值
  const getSettingValue = (key: string) => {
    const setting = contactSettings.find(s => s.key === key);
    return setting ? setting.value : '';
  };

  // 获取公司地址
  const companyAddress = getSettingValue('company_address');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        alert('感谢您的留言，我们会尽快与您联系！');
        // 清空表单
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        alert(`提交失败：${result.error}`);
      }
    } catch (error) {
      console.error('表单提交错误:', error);
      alert('网络错误，请稍后重试');
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
      <Navbar />
      
      {/* 页面头部 - 带背景图版 (与关于我们页面相同的设计) */}
      <section className="relative py-32 overflow-hidden">
        {/* 背景图片 - 使用联系我们分类的背景图，如果没有就不展示 */}
        {(() => {
          const backgroundImage = contactCategory?.backgroundImage;
          
          if (backgroundImage) {
            return (
              <div 
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: `url(${backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  filter: 'brightness(0.5) contrast(1.1)'
                }}
              >
                {/* 渐变遮罩层 */}
                <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              </div>
            );
          }
          return null;
        })()}
        
        {/* 动态背景装饰 */}
        <div className="absolute inset-0 opacity-15 z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-gradient-to-r from-orange-500/20 to-red-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 text-center z-20 mt-16">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent leading-tight drop-shadow-xl"
          >
            联系我们
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed drop-shadow-lg"
          >
            专业光学元件制造商，为您提供优质的产品和服务
          </motion.p>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent z-20"></div>
      </section>

      {/* 装饰性分割线 */}
      <div className="relative z-30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent mt-px"></div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="container mx-auto px-4 pt-20 pb-20">
        <div className="max-w-7xl mx-auto space-y-20">
          
          {/* 联系方式区域 - 横向布局 */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative bg-gradient-to-br from-gray-900/80 via-gray-800/60 to-gray-900/80 backdrop-blur-xl rounded-3xl p-10 border border-white/10 shadow-2xl shadow-blue-500/20"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/15 via-purple-500/10 to-transparent rounded-3xl"></div>
            <div className="absolute inset-0 border border-white/5 rounded-3xl"></div>
            <div className="absolute -top-1 -left-1 w-20 h-20 bg-gradient-to-br from-blue-500/20 to-transparent rounded-tl-3xl"></div>
            <div className="absolute -top-1 -right-1 w-20 h-20 bg-gradient-to-bl from-purple-500/20 to-transparent rounded-tr-3xl"></div>
            <div className="absolute -bottom-1 -left-1 w-20 h-20 bg-gradient-to-tr from-cyan-500/20 to-transparent rounded-bl-3xl"></div>
            <div className="absolute -bottom-1 -right-1 w-20 h-20 bg-gradient-to-tl from-orange-500/20 to-transparent rounded-br-3xl"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-white mb-12 text-center bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">联系方式</h2>
              
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div key={item} className="flex flex-col items-center text-center space-y-4">
                      <div className="w-20 h-20 bg-gray-700/50 rounded-2xl flex items-center justify-center animate-pulse">
                        <div className="w-8 h-8 bg-gray-600 rounded-full"></div>
                      </div>
                      <div className="w-24 h-4 bg-gray-700/50 rounded animate-pulse"></div>
                      <div className="w-32 h-3 bg-gray-700/50 rounded animate-pulse"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                  <motion.div 
                    className="flex flex-col items-center text-center space-y-4 group cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-orange-500/30 group-hover:shadow-orange-500/50 transition-all duration-500 transform group-hover:-rotate-3">
                        <span className="text-white text-2xl drop-shadow-lg">📍</span>
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-br from-orange-500 to-pink-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-orange-300 transition-colors duration-300">公司地址</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">{getSettingValue('company_address')}</p>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex flex-col items-center text-center space-y-4 group cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-500/30 group-hover:shadow-blue-500/50 transition-all duration-500 transform group-hover:rotate-3">
                        <span className="text-white text-2xl drop-shadow-lg">📞</span>
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-blue-300 transition-colors duration-300">联系电话</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">{getSettingValue('contact_phone')}</p>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex flex-col items-center text-center space-y-4 group cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-green-500 via-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-green-500/30 group-hover:shadow-green-500/50 transition-all duration-500 transform group-hover:-rotate-3">
                        <span className="text-white text-2xl drop-shadow-lg">✉️</span>
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-br from-green-500 to-cyan-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-green-300 transition-colors duration-300">电子邮箱</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">{getSettingValue('contact_email')}</p>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex flex-col items-center text-center space-y-4 group cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-purple-500/30 group-hover:shadow-purple-500/50 transition-all duration-500 transform group-hover:rotate-3">
                        <span className="text-white text-2xl drop-shadow-lg">⏰</span>
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-purple-300 transition-colors duration-300">工作时间</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">{getSettingValue('work_hours')}</p>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex flex-col items-center text-center space-y-4 group cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-green-400 via-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-emerald-500/30 group-hover:shadow-emerald-500/50 transition-all duration-500 transform group-hover:-rotate-3">
                        <span className="text-white text-2xl drop-shadow-lg">💬</span>
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-emerald-300 transition-colors duration-300">微信客服</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">{getSettingValue('wechat_customer_service')}</p>
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          </motion.div>

          {/* 联系表单区域 - 独占一行 */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative bg-gradient-to-br from-gray-900/80 via-gray-800/60 to-gray-900/80 backdrop-blur-xl rounded-3xl p-10 border border-white/10 shadow-2xl shadow-blue-500/20"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/15 via-purple-500/10 to-transparent rounded-3xl"></div>
            <div className="absolute inset-0 border border-white/5 rounded-3xl"></div>
            <div className="absolute -top-1 -left-1 w-20 h-20 bg-gradient-to-br from-blue-500/20 to-transparent rounded-tl-3xl"></div>
            <div className="absolute -top-1 -right-1 w-20 h-20 bg-gradient-to-bl from-purple-500/20 to-transparent rounded-tr-3xl"></div>
            <div className="absolute -bottom-1 -left-1 w-20 h-20 bg-gradient-to-tr from-cyan-500/20 to-transparent rounded-bl-3xl"></div>
            <div className="absolute -bottom-1 -right-1 w-20 h-20 bg-gradient-to-tl from-orange-500/20 to-transparent rounded-br-3xl"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-white mb-12 text-center bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">留言咨询</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div 
                    whileFocus={{ scale: 1.02 }}
                    className="relative"
                  >
                    <label className="block text-white font-semibold mb-3 text-sm tracking-wide">您的姓名 *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-5 py-4 bg-gradient-to-br from-gray-800/60 to-gray-700/40 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/50 transition-all duration-300 backdrop-blur-sm shadow-lg shadow-gray-900/20"
                      placeholder="请输入您的姓名"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-transparent rounded-xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </motion.div>

                  <motion.div 
                    whileFocus={{ scale: 1.02 }}
                    className="relative"
                  >
                    <label className="block text-white font-semibold mb-3 text-sm tracking-wide">您的公司名称</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 bg-gradient-to-br from-gray-800/60 to-gray-700/40 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 backdrop-blur-sm shadow-lg shadow-gray-900/20"
                      placeholder="请输入您的公司名称"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent rounded-xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </motion.div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div 
                    whileFocus={{ scale: 1.02 }}
                    className="relative"
                  >
                    <label className="block text-white font-semibold mb-3 text-sm tracking-wide">您的邮箱 *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-5 py-4 bg-gradient-to-br from-gray-800/60 to-gray-700/40 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/50 transition-all duration-300 backdrop-blur-sm shadow-lg shadow-gray-900/20"
                      placeholder="请输入您的邮箱地址"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent rounded-xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </motion.div>

                  <motion.div 
                    whileFocus={{ scale: 1.02 }}
                    className="relative"
                  >
                    <label className="block text-white font-semibold mb-3 text-sm tracking-wide">您的电话</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 bg-gradient-to-br from-gray-800/60 to-gray-700/40 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all duration-300 backdrop-blur-sm shadow-lg shadow-gray-900/20"
                      placeholder="请输入您的联系电话"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent rounded-xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </motion.div>
                </div>

                <motion.div 
                  whileFocus={{ scale: 1.02 }}
                  className="relative"
                >
                  <label className="block text-white font-semibold mb-3 text-sm tracking-wide">留言内容 *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-5 py-4 bg-gradient-to-br from-gray-800/60 to-gray-700/40 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 transition-all duration-300 backdrop-blur-sm shadow-lg shadow-gray-900/20 resize-none"
                    placeholder="请描述您的需求或问题，我们会尽快与您联系..."
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </motion.div>

                <motion.div className="pt-4">
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300 relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10 flex items-center justify-center space-x-2">
                      <span>发送留言</span>
                      <motion.span 
                        className="text-xl"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        →
                      </motion.span>
                    </div>
                  </motion.button>
                </motion.div>
              </form>
            </div>
          </motion.div>

          {/* 地图区域 */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="relative bg-gradient-to-br from-gray-900/80 via-gray-800/60 to-gray-900/80 backdrop-blur-xl rounded-3xl p-10 border border-white/10 shadow-2xl shadow-blue-500/20"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/15 via-purple-500/10 to-transparent rounded-3xl"></div>
            <div className="absolute inset-0 border border-white/5 rounded-3xl"></div>
            <div className="absolute -top-1 -left-1 w-20 h-20 bg-gradient-to-br from-blue-500/20 to-transparent rounded-tl-3xl"></div>
            <div className="absolute -top-1 -right-1 w-20 h-20 bg-gradient-to-bl from-purple-500/20 to-transparent rounded-tr-3xl"></div>
            <div className="absolute -bottom-1 -left-1 w-20 h-20 bg-gradient-to-tr from-cyan-500/20 to-transparent rounded-bl-3xl"></div>
            <div className="absolute -bottom-1 -right-1 w-20 h-20 bg-gradient-to-tl from-orange-500/20 to-transparent rounded-br-3xl"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-white mb-8 text-center bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">地理位置</h2>
              <div className="aspect-video bg-gradient-to-br from-gray-800/40 to-gray-700/40 rounded-2xl border border-gray-600/50 backdrop-blur-sm overflow-hidden">
                <BaiduMap 
                  apiKey={process.env.NEXT_PUBLIC_BAIDU_MAP_API_KEY || "demo_key"}
                  center={{ lng: 106.567844, lat: 29.666893 }}
                  zoom={16}
                  className="w-full h-full"
                  companyAddress={getSettingValue('company_address')}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* 底部 */}
      <Footer />
    </main>
  )
}